import { supabase } from '../lib/supabase';

export interface ImageUploadResult {
  success: boolean;
  imageUrl?: string;
  imagePath?: string;
  error?: string;
  imageData?: {
    filename: string;
    size: number;
    mimeType: string;
    width: number;
    height: number;
  };
}

export class ImageUploadService {
  /**
   * Upload an image file to Supabase storage
   */
  static async uploadImage(
    file: File,
    userId: string,
    messageId: string
  ): Promise<ImageUploadResult> {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'File must be an image'
        };
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'Image size must be less than 10MB'
        };
      }

      // Get file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Generate unique path (fallback if database function doesn't exist)
      let imagePath: string;
      
      try {
        const { data: pathData, error: pathError } = await supabase
          .rpc('generate_message_image_path', {
            user_id: userId,
            message_id: messageId,
            file_extension: fileExtension
          });

        if (pathError) {
          console.warn('Database function not available, using fallback path generation:', pathError);
          // Fallback path generation
          const timestamp = Date.now();
          imagePath = `messages/${userId}/${messageId}_${timestamp}.${fileExtension}`;
        } else {
          imagePath = pathData;
        }
      } catch (error) {
        console.warn('Error calling database function, using fallback:', error);
        // Fallback path generation
        const timestamp = Date.now();
        imagePath = `messages/${userId}/${messageId}_${timestamp}.${fileExtension}`;
      }

      // Upload file to storage
      console.log('Uploading image to path:', imagePath);
      console.log('Using bucket: message-images');
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('message-images')
        .upload(imagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        
        // Check if it's a bucket not found error
        if (uploadError.message.includes('Bucket not found') || uploadError.message.includes('message-images')) {
          return {
            success: false,
            error: 'Storage bucket not found. Please run the database migrations first.'
          };
        }
        
        return {
          success: false,
          error: `Failed to upload image: ${uploadError.message}`
        };
      }

      console.log('Image uploaded successfully:', uploadData);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('message-images')
        .getPublicUrl(imagePath);

      // Get image dimensions
      const imageDimensions = await this.getImageDimensions(file);

      return {
        success: true,
        imageUrl: urlData.publicUrl,
        imagePath: imagePath,
        imageData: {
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          width: imageDimensions.width,
          height: imageDimensions.height
        }
      };

    } catch (error) {
      console.error('Error in uploadImage:', error);
      return {
        success: false,
        error: 'An unexpected error occurred'
      };
    }
  }

  /**
   * Get image dimensions from a file
   */
  private static async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => {
        resolve({ width: 0, height: 0 });
      };
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Delete an image from storage
   */
  static async deleteImage(imagePath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from('message-images')
        .remove([imagePath]);

      if (error) {
        console.error('Error deleting image:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteImage:', error);
      return false;
    }
  }

  /**
   * Get image URL from path
   */
  static getImageUrl(imagePath: string): string {
    const { data } = supabase.storage
      .from('message-images')
      .getPublicUrl(imagePath);
    
    return data.publicUrl;
  }

  /**
   * Validate image file
   */
  static validateImageFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Please select an image file' };
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, error: 'Image size must be less than 10MB' };
    }

    // Check supported formats
    const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!supportedTypes.includes(file.type)) {
      return { valid: false, error: 'Unsupported image format. Please use JPEG, PNG, GIF, WebP, or SVG' };
    }

    return { valid: true };
  }
}
